<template>
    <BaseLayout :heading="'CASI - Pentagons'" />

    <div class="casi-pentagons">
      <CASIQuestionsSection
        v-if="questions.length > 0"
        :questions="questions"
        @score-added="handleChanged"
        @input-changed="handleChanged"
      />
    </div>

    <NotesSection
      :administeringNotes="jsonData ? jsonData.administeringNotes : ''"
      :scoringNotes="jsonData ? jsonData.scoringNotes : ''"
      :visitId="visitId"
      :taskType="'CASI Pentagons Task'"
      ref="notesSection" />

    <TextButton buttonText="Submit" :dark="true" type="submit" @click="submitForm" />

    <NotificationPopup
      :message="notificationMessage"
      :visible="notificationVisible"
    />

    <ConfirmationPopup
      :visible="showConfirmPopup"
      @confirm="handleConfirmLeave"
      @noSave="handleNoSaveLeave"
      @cancel="showConfirmPopup = false"
    />
</template>

<script>
import BaseLayout from './BaseLayout.vue';
import NotesSection from '../components/NotesSection.vue';
import CASIQuestionsSection from '../components/CASI/CASIQuestionsSection.vue';
import TaskController from '../controllers/TaskController.js';
import TextButton from '@/components/TextButton.vue';
import NotificationPopup from '@/components/NotificationPopup.vue';
import ConfirmationPopup from '@/components/ConfirmationPopup.vue';
import blankPentagonData from '../data/casiPentagon.json';

const LOG_TYPE = 'InterviewerCASIPentagonLog';
const STORAGE_KEY = 'casiPentagonData';

export default {
  components: {
      BaseLayout,
      NotesSection,
      CASIQuestionsSection,
      TextButton,
      NotificationPopup,
      ConfirmationPopup
  },
  data() {
    return {
      controller: new TaskController(LOG_TYPE, STORAGE_KEY),
      jsonData: null,
      notificationMessage: '',
      notificationVisible: false,
      showConfirmPopup: false,
      hasUnsavedChanges: false,
      nextRoute: null,
      forceNavigate: false
    };
  },
  computed: {
    visitId() {
      return localStorage.getItem('visitId');
    },
    isAdministering() {
        return this.$route.query.selection === 'administer';
    },
    questions() {
      const sections = this.jsonData && this.jsonData.sections;
      if (!Array.isArray(sections)) return [];
      return sections.flatMap(section => section.questions || []);
    }
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    async loadData() {
      await this.controller.loadData();
      const loaded = this.controller.model.data;

      // ServerCodeModel only seeds the blank task templates when a participant has no
      // prior logs; on every other path this task would start with an empty object.
      if (!loaded || !Array.isArray(loaded.sections)) {
        this.controller.model.data = JSON.parse(JSON.stringify(blankPentagonData));
      }

      this.jsonData = this.controller.model.data;
    },
    handleChanged() {
      this.hasUnsavedChanges = true;
    },
    notesChanged() {
      const notesSection = this.$refs.notesSection;
      if (!notesSection || !this.jsonData) return false;

      return this.isAdministering
        ? notesSection.getAdministeringNotes() !== this.jsonData.administeringNotes
        : notesSection.getScoringNotes() !== this.jsonData.scoringNotes;
    },
    async submitForm() {
      this.notificationMessage = 'Saving...';
      this.notificationVisible = true;

      if (this.jsonData.timeCreated === '') {
        this.jsonData.timeCreated = new Date().toUTCString();
      }
      this.jsonData.timeLastModified = new Date().toUTCString();

      const notesSection = this.$refs.notesSection;
      if (notesSection) {
        this.jsonData.administeringNotes = notesSection.getAdministeringNotes() || '';
        this.jsonData.scoringNotes = notesSection.getScoringNotes() || '';
      }

      try {
        await this.controller.submitForm();
        this.hasUnsavedChanges = false;
        this.notificationMessage = 'CASI Pentagons Saved Successfully!';
      } catch (error) {
        this.notificationMessage = 'Saving failed. Please try again.';
        console.error('Error submitting CASI Pentagons:', error);
      } finally {
        this.resetNotification();
      }
    },
    resetNotification() {
      setTimeout(() => {
        this.notificationVisible = false;
      }, 3000);
    },
    async handleConfirmLeave() {
      this.showConfirmPopup = false;
      await this.submitForm();
      this.$router.push(this.nextRoute);
    },
    handleNoSaveLeave() {
      this.showConfirmPopup = false;
      this.forceNavigate = true;
      this.hasUnsavedChanges = false;
      this.$router.push(this.nextRoute);
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.forceNavigate) {
      this.forceNavigate = false;
      next();
      return;
    }

    if (this.hasUnsavedChanges || this.notesChanged()) {
      this.showConfirmPopup = true;
      this.nextRoute = to.fullPath;
      next(false);
    } else {
      this.controller.model.saveData(this.jsonData);
      next();
    }
  }
};
</script>

<style scoped>
.casi-pentagons {
  max-width: 1200px;
  margin: auto;
}
</style>
